import {filter, takeUntil} from 'rxjs/operators';
import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {select} from '@angular-redux/store';
import {lastValueFrom, Observable, Subject} from 'rxjs';
import {
    Account,
    AuthData,
    BillingDetails,
    Company,
    PaymentHistoryYears,
    Profile,
    Subscription
} from "sharedFeatures/sharedModels";
import {CheckSubscriptionDto} from 'sharedFeatures/sharedModels/CheckSubscription';
import {
    IBillingDetails,
    ICompaniesResponse,
    ICompanyBaseResponse,
    ICreateCompanyResponse,
    ICustomerLicensesResponse,
    IErrorResponse,
    IPaymentHistoryResponse,
    IProfileResponse,
    ISpareLicensesResponse,
    ISSOLinkResponse,
    isSuccessResponse,
    ISubscription,
    ISubscriptionDowngradeResponse,
    ISubscriptionResponse,
} from "sharedFeatures/sharedServices/Responses/billing.index";
import { IAssignCompanyLicenseResponse, IRemoveCompanyLicenseResponse, IStartTrialResponse } from './Responses';

@Injectable()
export class BillingService implements OnDestroy {

    UserSubscriptionPromise: Promise<Array<ISubscription> | undefined> = Promise.resolve<Array<Subscription>>([]);

    // Unsubscribe event
    private storeUnsubscribe = new Subject<void>();

    @select(['account', 'accountData'])
    readonly accountData$!: Observable<Account>;
    private accountData: Account = new Account();

    @select(['account', 'authData'])
    readonly authData$!: Observable<AuthData>;
    authData: AuthData = new AuthData();

    constructor(private http: HttpClient) {
        // Subscribe on the redux store
        this.subscribeReduxStore();
    }

    ngOnDestroy() {
        // Unsubscribe from the redux store
        this.storeUnsubscribe.next();
        this.storeUnsubscribe.complete();
    }

    // Subscribe on the redux store
    private subscribeReduxStore(): void {
        this.accountData$?.pipe(
            takeUntil(this.storeUnsubscribe))
            ?.subscribe(x => this.accountData = x);

        this.authData$?.pipe(
            filter(x => !!x))
            ?.subscribe(authData => {
                this.authData = authData;
            });
    }

    LoadBillingDetails(customerRef: string): Promise<IBillingDetails> {
        let params = new HttpParams()
            .set('customerRef', customerRef)
            .set('userID', this.authData.userID.toString());

        const response = this.http.get<string>(`${window.coreApiUrl}/Subscription/GetBillingDetails`, { params });
        return lastValueFrom(response)
            .then((res: string) => {
                let data: IBillingDetails;

                if (res && res.length)
                    data = JSON.parse(res);
                else
                    data = new BillingDetails();

                return data;
            });
    }

    SaveBillingDetails(billingDetails: IBillingDetails): Promise<string> {
        const response = this.http.post<string>(`${window.appPath}Subscription/SaveBillingDetails`, billingDetails);
        return lastValueFrom(response);
    }

    SaveProfile(profile: Profile): Promise<Profile | undefined> {
        const response = this.http.post<string>(`${window.appPath}Subscription/SaveProfile`, profile);
        return lastValueFrom(response)
            .then((res: string) => {
                let data: Profile | undefined;

                if (res && res.length) {
                    let profileResponse: IProfileResponse = JSON.parse(res);
                    if (isSuccessResponse(profileResponse) && profileResponse.data)
                        data = profileResponse.data;
                }

                return data;
            });
    }

    GetPricesTable(productCode: string): Promise<any> {
        let params = new HttpParams();
        const isGetTablePrices = true;
        params = params
            .set('productCode', productCode)

        const response = this.http.get<string>(`${window.coreApiUrl}/Subscription/GetPriceTable`, { params });
        return lastValueFrom(response)
            .then((res: string) => {
                if (res && res.length) {
                    console.log('GetPricesTable:', JSON.parse(res));
                    return JSON.parse(res);
                }
            });
    }

    LoadCompanies(includeOthersWithAccessCompanies: boolean = true): Promise<ICompaniesResponse | IErrorResponse> {
        let params = new HttpParams()            
            .set('includeOthersWithAccessCompanies', includeOthersWithAccessCompanies+'');

        const response = this.http.get<string>(`${window.appPath}Subscription/GetCompanies`, { params });
        return lastValueFrom(response)
            .then((res: string) => {
                let data: ICompaniesResponse | IErrorResponse = {
                    success: false, errorMessage: ''
                };

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    SaveCompany(company: Company): Promise<ICompanyBaseResponse | IErrorResponse> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set('companyJson', JSON.stringify(company));
        let body = params.toString();

        const response = this.http.post<string>(`${window.appPath}Subscription/SaveCompany`, body, { headers: headers })
        return lastValueFrom(response)
            .then((res: string) => {
                let data: ICompanyBaseResponse | IErrorResponse = { success: false, errorMessage: '' };

                if (res && res.length)
                    data = JSON.parse(res);

                return data;
            });
    }

    RemoveCompany(companyId: number): Promise<ICompanyBaseResponse | IErrorResponse> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set('companyId', JSON.stringify(companyId));
        let body = params.toString();

        const response = this.http.post<string>(`${window.appPath}Subscription/RemoveCompany`, body, { headers: headers })
        return lastValueFrom(response)
            .then((res: string) => {
                let data: ICompanyBaseResponse | IErrorResponse = { success: false, errorMessage: '' };

                if (res && res.length)
                    data = JSON.parse(res);

                return data;
            });
    }

    LoadSubscriptions(forceRefresh: boolean = false): void {
        let params = new HttpParams();
        if (this.authData && this.authData.userID && this.authData.userID != 0) {
            params = params
                .set('customerId', this.authData.userID.toString())
                .set('forceRefresh', forceRefresh.toString())
                .set('userID', this.authData.userID.toString());

            const response = this.http.get<string>(`${window.coreApiUrl}/Subscription/Index`, { params });
            this.UserSubscriptionPromise = lastValueFrom(response)
                .then((res: string) => {
                    if (res && res.length) {
                        console.log('LoadSubscription error ', JSON.parse(res));
                        return JSON.parse(res) as Array<ISubscription>;
                    }
                });
        }
    }

    LoadSSOLink(subscriptionReference: string, pageType: string): Promise<ISSOLinkResponse | IErrorResponse> {
        let params = new HttpParams()
            .set('subscriptionReference', subscriptionReference)
            .set('pageType', pageType);

        const response = this.http.get<string>(`${window.appPath}Subscription/GetSSOLink`, { params });
        return lastValueFrom(response)
            .then((res: string) => {
                let data: ISSOLinkResponse | IErrorResponse = { success: false, errorMessage: '', ssoLink: '' };

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    LoadPaymentHistoryYears(): Promise<PaymentHistoryYears> {
        if (this.accountData.customerRef <= 0)
            return Promise.resolve( new PaymentHistoryYears());

        let params = new HttpParams()
            .set('customerRef', this.accountData.customerRef.toString());

        const response = this.http.get<string>(`${window.appPath}Subscription/GetPaymentHistoryYears`, { params });
        return lastValueFrom(response)
            .then((res: string) => {
                let data: PaymentHistoryYears = new PaymentHistoryYears();

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    LoadPaymentHistory(year: string, subscriptionRef: string): Promise<IPaymentHistoryResponse | undefined> {
        if (this.accountData.customerRef <= 0)
            return Promise.resolve(undefined);

        let params = new HttpParams()
            .set('customerRef', this.accountData.customerRef.toString())
            .set('year', year)
            .set('subscriptionRef', subscriptionRef)
            .set('userID', this.authData.userID.toString())

        const response = this.http.get<string>(`${window.coreApiUrl}/Subscription/GetPaymentHistory`, { params });
        return lastValueFrom(response)
            .then((res: string) => {
                let data: IPaymentHistoryResponse | undefined = { success: false, errorMessage: '', history: []};

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    ChangeSubscriptionAutoRenew(subscriptionRef: string, autoRenew: boolean): Promise<ISubscriptionResponse | IErrorResponse> {
        if (this.accountData.customerRef <= 0)
            return Promise.resolve({ success: false, errorMessage: 'customerRef is undefined' });

        let params = new HttpParams()
            .set('subscriptionRef', subscriptionRef)
            .set('customerRef', this.accountData.customerRef.toString())
            .set('autoRenew', autoRenew.toString());

        const response = this.http.get<string>(`${window.appPath}Subscription/ChangeSubscriptionAutoRenew`, { params });
        return lastValueFrom(response)
            .then((res) => {
                let data: ISubscriptionResponse | IErrorResponse = { success: false, errorMessage: '' };

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    ScheduleSubscriptionDowngrade(
        customerRef: number,
        subscriptionRef: string,
        productID: string,
        priceOption1: string,
        priceOption2: string): Promise<ISubscriptionDowngradeResponse | IErrorResponse>
    {
        if (this.accountData.customerRef <= 0)
            return Promise.resolve({ success: false, errorMessage: 'customerRef is undefined' });

        let params = new HttpParams()
            .set('customerRef', customerRef.toString())
            .set('subscriptionRef', subscriptionRef)
            .set('productID', productID)
            .set('priceOption1', priceOption1)
            .set('priceOption2', priceOption2);

        const response = this.http.get<string>(`${window.appPath}Subscription/ScheduleSubscriptionDowngrade`, { params });
        return lastValueFrom(response)
            .then((res: string) => {
                let data: ISubscriptionDowngradeResponse | IErrorResponse = { success: false, errorMessage: '', downgradeDate: '' };

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    CancelSubscriptionDowngrade(subscriptionRef: string) {
        if (!this.accountData.subscriptionRef || !this.accountData.subscriptionRef.length)
            return Promise.resolve({ success: false, errorMessage: 'subscriptionRef is undefined' });

        let params = new HttpParams()
            .set('customerRef', this.accountData.customerRef.toString())
            .set('subscriptionRef', subscriptionRef);

        const response = this.http.get<string>(`${window.appPath}Subscription/CancelSubscriptionDowngrade`, { params });
        return lastValueFrom(response)
            .then((res: string) => {
                let data: ISubscriptionResponse | IErrorResponse = { success: false, errorMessage: '' };

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    CheckSubscriptionUpgradeStatus(userId: string, subscriptionRef: string, companyId: string): Promise<CheckSubscriptionDto> {
        let params = new HttpParams()
            .set('customerId', userId)
            .set('subscriptionRef', subscriptionRef)
            .set('companyId', companyId);            

        const response = this.http.get<string>(`${window.appPath}Subscription/CheckSubscriptionUpgradeStatus`, { params });
        return lastValueFrom(response)
            .then((res: string) => {
                let data: CheckSubscriptionDto = new CheckSubscriptionDto();

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    RemoveCompanyLicense(companyId: string) {
        let params = new HttpParams()
            .set('companyId', companyId);

        const response = this.http.get<string>(`${window.appPath}Subscription/RemoveCompanyLicense`, { params });
        return lastValueFrom(response)
            .then((res: string) => {
                let data: IRemoveCompanyLicenseResponse | IErrorResponse = { success: false, errorMessage: '' };

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    AssignCompanyLicense(licenseId: string, companyId: string) {
        let params = new HttpParams()
            .set('licenseId', licenseId)
            .set('companyId', companyId);

        const response = this.http.get<string>(`${window.appPath}Subscription/AssignCompanyLicense`, { params })
        return lastValueFrom(response)
            .then((res: string) => {
                let data: IAssignCompanyLicenseResponse | IErrorResponse = { success: false, errorMessage: '' };

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    GetSpareLicenses(): Promise<ISpareLicensesResponse | IErrorResponse> {
        const response = this.http.get<string>(`${window.appPath}Subscription/GetSpareLicenses`, { });
        return lastValueFrom(response)
            .then((res: string) => {
                let data: ISpareLicensesResponse | IErrorResponse = { success: false, errorMessage: '', spareLicenses: [] };

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    GetCustomerLicenses(): Promise<ICustomerLicensesResponse | IErrorResponse> {
        const response = this.http.get<string>(`${window.appPath}Subscription/GetCustomerLicenses`, {})
        return lastValueFrom(response)
            .then((res: string) => {
                let data: ICustomerLicensesResponse | IErrorResponse = { success: false, errorMessage: '', customerLicenses: [] };

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    CreateCompany(
        companyName: string,
        industryId: number,
        countryId: number,
        webSite: string,
        createTrial: boolean = false
    ): Promise<ICreateCompanyResponse | IErrorResponse>
    {
        let params = new HttpParams()
            .set('companyName', companyName)
            .set('industryId', industryId.toString())
            .set('countryId', countryId.toString())
            .set('webSite', webSite)
            .set('createTrial', createTrial.toString());

        const response = this.http.get<string>(`${window.appPath}Subscription/CreateCompany`, { params })
        return lastValueFrom(response)
            .then((res: string) => {
                let data: ICreateCompanyResponse | IErrorResponse = { success: false, errorMessage: '' };

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

    StartTrial(companyId: string) {
        let params = new HttpParams()
            .set('companyId', companyId);

        const response = this.http.get<string>(`${window.appPath}Subscription/StartTrial`, { params });
        return lastValueFrom(response)
            .then((res) => {
                let data: IStartTrialResponse | IErrorResponse = { success: false, errorMessage: '' };

                if (res && res.length) {
                    data = JSON.parse(res);
                }

                return data;
            });
    }

}
